import { LocalizedInputWithLabel } from '@components/common/LocalizedInputWithLabel';
import InputWithLabelT from '@components/tailwind-components/Input/InputWithLabelT';
import SelectInputWithLabelT from '@components/tailwind-components/Input/SelectInputWithLabelT';
import { getSelectedLanguage } from '@config/i18n';
import { URL_WITH_PROTOCOL_REGEX } from '@constants/regex';
import type { TMenuEntry } from '@interfaces/redux/TDesignerState';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMainSelector } from '../../../../../hooks/useMainSelector';

type Props = {
  name: string;
  watch: any;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  selectedRevisionId: string;
  inputClassName?: string;
  labelClassName?: string;
} & React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

export const WebclientMenuList: React.FC<Props> = ({
  name,
  watch,
  control,
  setValue,
  selectedRevisionId,
  inputClassName = '',
  labelClassName = '',
  ...rest
}) => {
  const { t } = useTranslation();
  const language = getSelectedLanguage();
  const payloadType: 'payload' | 'webLink' = watch(`${name}.type`);
  const payloadValue = watch(`${name}.payload`);
  const {
    designer: { menuEntries: destinations, revisions },
  } = useMainSelector();
  const [selectableDestinations, setSelectableDestinations] = useState<
    TMenuEntry[]
  >([]);
  const [prevSelectedRevisionId, setPrevSelectedRevisionId] = useState<
    string | null
  >(null);

  const defaultRevision = useMemo(
    () => (revisions || []).find((revision) => revision.isMainRevision),
    [revisions],
  ) || { _id: '' };

  // Registered on both the URL input and the destination select below via the
  // same key, so react-hook-form's field record is fully replaced on every
  // type switch instead of leaving a stale rule (e.g. a URL pattern) attached
  // when the destination is later rendered by the other input.
  const validatePayload = (value: string) => {
    if (!value?.trim()) {
      return t('settings.webclient.config.form.config.menu.payload.required');
    }

    if (
      payloadType === 'webLink' &&
      !URL_WITH_PROTOCOL_REGEX.test(value.trim())
    ) {
      return t('settings.webclient.config.form.config.menu.payload.invalid');
    }

    return true;
  };

  useEffect(() => {
    const selectableDestinations = destinations.filter(
      (destination) =>
        (destination.revisionId === selectedRevisionId ||
          (!destination.revisionId &&
            selectedRevisionId === defaultRevision._id)) &&
        destination.subFlow,
    );
    setSelectableDestinations(selectableDestinations);
  }, [selectedRevisionId, destinations, defaultRevision._id]);

  useEffect(() => {
    if (payloadType !== 'payload') {
      return;
    }

    if (
      prevSelectedRevisionId !== null &&
      prevSelectedRevisionId !== selectedRevisionId
    ) {
      // reset the payload value when the revision is changed
      setValue(`${name}.payload`, '');
    }

    setPrevSelectedRevisionId(selectedRevisionId);
  }, [selectedRevisionId, payloadType, setValue, name]);

  return (
    <div {...rest}>
      <SelectInputWithLabelT
        field={`${name}.type`}
        label={t('settings.webclient.config.form.config.menu.type')}
        control={control}
        values={[
          { value: 'payload', description: 'Payload' },
          { value: 'webLink', description: 'URL' },
        ]}
        options={{
          onChange: () => {
            // reset the payload value when the type is changed
            setValue(`${name}.payload`, '');
          },
        }}
        className={inputClassName}
        labelClassName={labelClassName}
      />

      <LocalizedInputWithLabel
        field={`${name}.title`}
        id={`${name}-title`}
        placeholder={t('settings.webclient.config.form.config.menu.title')}
        label={t('settings.webclient.config.form.config.menu.title')}
        control={control}
        inputClassName={inputClassName}
        labelClassName={labelClassName}
        required={t(
          'settings.webclient.config.form.config.menu.title.required',
        )}
      />

      {payloadType === 'webLink' ? (
        <InputWithLabelT
          type='text'
          field={`${name}.payload`}
          placeholder={t('settings.webclient.config.form.config.menu.payload')}
          label={t('settings.webclient.config.form.config.menu.payload')}
          control={control}
          inputClassName={inputClassName}
          labelClassName={labelClassName}
          options={{ validate: validatePayload }}
        />
      ) : (
        <SelectInputWithLabelT
          field={`${name}.payload`}
          label={t('settings.webclient.config.form.config.menu.payload')}
          control={control}
          value={payloadValue}
          values={selectableDestinations.map((destination) => ({
            value: destination.id,
            description: `${destination.text[language] || destination.id} (${destination.subFlow})`,
          }))}
          options={{
            disabled: !selectableDestinations?.length,
            validate: validatePayload,
          }}
          className={inputClassName}
          labelClassName={labelClassName}
        />
      )}
    </div>
  );
};

export default WebclientMenuList;
